import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils';

type AccordionContextType = {
  value: string[];
  onValueChange: (value: string[]) => void;
  type: 'single' | 'multiple';
  collapsible: boolean;
};

const AccordionContext = React.createContext<AccordionContextType>({
  value: [],
  onValueChange: () => {},
  type: 'single',
  collapsible: false,
});

interface AccordionProps {
  type?: 'single' | 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  collapsible?: boolean;
  className?: string;
  children: React.ReactNode;
}

const Accordion = ({
  type = 'single',
  value,
  defaultValue = [],
  onValueChange,
  collapsible = true,
  className,
  children,
  ...props
}: AccordionProps) => {
  const [internalValue, setInternalValue] = React.useState<string[]>(defaultValue);
  
  const currentValue = value !== undefined ? value : internalValue;
  
  const handleValueChange = React.useCallback((newValue: string[]) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  }, [onValueChange]);

  return (
    <AccordionContext.Provider value={{ value: currentValue, onValueChange: handleValueChange, type, collapsible }}>
      <div className={cn('flex flex-col', className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};
Accordion.displayName = 'Accordion';

interface AccordionItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('border-b', className)}
        data-value={value}
        {...props}
      >
        {children}
      </div>
    );
  }
);
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(AccordionContext);
    const itemElement = React.useRef<HTMLDivElement | null>(null);
    const [itemValue, setItemValue] = React.useState('');
    
    React.useEffect(() => {
      let el = ref as React.MutableRefObject<HTMLDivElement | null>;
      while (el && el.current) {
        if (el.current.getAttribute('data-value')) {
          setItemValue(el.current.getAttribute('data-value') || '');
          break;
        }
        el = { current: el.current.parentElement as HTMLDivElement | null };
      }
    }, [ref]);

    const isOpen = context.value.includes(itemValue);

    const handleClick = () => {
      if (context.type === 'single') {
        if (isOpen && context.collapsible) {
          context.onValueChange([]);
        } else {
          context.onValueChange([itemValue]);
        }
      } else {
        if (isOpen) {
          context.onValueChange(context.value.filter(v => v !== itemValue));
        } else {
          context.onValueChange([...context.value, itemValue]);
        }
      }
    };
    
    return (
      <button
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
          className
        )}
        data-state={isOpen ? 'open' : 'closed'}
        onClick={handleClick}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </button>
    );
  }
);
AccordionTrigger.displayName = 'AccordionTrigger';

interface AccordionContentProps {
  className?: string;
  children: React.ReactNode;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(AccordionContext);
    const itemElement = React.useRef<HTMLDivElement | null>(null);
    const [itemValue, setItemValue] = React.useState('');
    
    React.useEffect(() => {
      let el = ref as React.MutableRefObject<HTMLDivElement | null>;
      while (el && el.current) {
        if (el.current.getAttribute('data-value')) {
          setItemValue(el.current.getAttribute('data-value') || '');
          break;
        }
        el = { current: el.current.parentElement as HTMLDivElement | null };
      }
    }, [ref]);

    const isOpen = context.value.includes(itemValue);
    
    if (!isOpen) return null;
    
    return (
      <div
        ref={ref}
        className={cn('overflow-hidden text-sm transition-all', className)}
        {...props}
      >
        <div className="pb-4 pt-0">{children}</div>
      </div>
    );
  }
);
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };